import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { hasArVersion, isArPath, toArPath, toEnPath } from '../i18nRoutes';

const LOCALES = ['en_US', 'ar_SA', 'ru_RU', 'tr_TR'];

const SEGMENT_NAMES = {
  'tours': 'Tours',
  'istanbul': 'Istanbul',
  'blog': 'Blog',
  'real-estate': 'Real Estate',
  'about': 'About',
  'faq': 'FAQ',
  'travel': 'Tours & Travel',
  'ar': 'العربية',
};

function humanizeSegment(seg) {
  if (SEGMENT_NAMES[seg]) return SEGMENT_NAMES[seg];
  return seg.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function buildBreadcrumb(url) {
  let path;
  try {
    path = new URL(url).pathname.replace(/\/$/, '');
  } catch {
    return null;
  }
  if (!path) return null;
  const segs = path.split('/').filter(Boolean);
  const items = [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://viaturkey.net/" }];
  let acc = '';
  segs.forEach((seg, i) => {
    acc += `/${seg}`;
    items.push({ "@type": "ListItem", "position": i + 2, "name": humanizeSegment(seg), "item": `https://viaturkey.net${acc}` });
  });
  return { "@type": "BreadcrumbList", "itemListElement": items };
}

export function SEOHead({ title, description, canonical, image, type = 'website', schema }) {
  const defaultImage = 'https://viaturkey.net/images/viaturkey-logo.jpg';
  const img = image || defaultImage;
  const safeTitle = title || 'Viaturkey';

  const onArRoute = typeof window !== 'undefined' && isArPath(window.location.pathname);
  const baseCanonical = canonical || 'https://viaturkey.net/';
  let enPath = '/';
  try { enPath = toEnPath(new URL(baseCanonical).pathname); } catch { /* keep default */ }
  const enUrl = `https://viaturkey.net${enPath === '/' ? '/' : enPath}`;
  const arUrl = `https://viaturkey.net${toArPath(enPath)}`;
  const url = onArRoute ? arUrl : enUrl;
  const pairExists = hasArVersion(enPath);

  // Single consolidated JSON-LD @graph per page, with auto BreadcrumbList below the homepage
  useEffect(() => {
    const nodes = [];
    if (schema) {
      if (Array.isArray(schema['@graph'])) {
        nodes.push(...schema['@graph']);
      } else {
        const { ['@context']: _ctx, ...rest } = schema;
        nodes.push(rest);
      }
    }
    const hasBreadcrumb = JSON.stringify(nodes).includes('"BreadcrumbList"');
    if (!hasBreadcrumb) {
      const bc = buildBreadcrumb(url);
      if (bc) nodes.push(bc);
    }
    if (!nodes.length) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-head', 'true');
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": nodes });
    document.head.appendChild(script);
    return () => {
      document.head.querySelectorAll('script[data-seo-head="true"]').forEach(el => el.remove());
    };
  }, [schema, url]);

  return (
    <Helmet title={safeTitle}>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {pairExists && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {pairExists && <link rel="alternate" hrefLang="ar" href={arUrl} />}
      {pairExists && <link rel="alternate" hrefLang="x-default" href={enUrl} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content="Viaturkey" />
      <meta property="og:locale" content={onArRoute ? 'ar_SA' : 'en_US'} />
      {LOCALES.filter(l => l !== (onArRoute ? 'ar_SA' : 'en_US')).map(l => <meta key={l} property="og:locale:alternate" content={l} />)}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta name="twitter:site" content="@akngzl3" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
    </Helmet>
  );
}
